import { buildRoute } from "@u/routes";

export type NotificationType =
  | "new_lead"
  | "new_booking"
  | "booking_confirmed"
  | "booking_reminder"
  | "milestone_visits"
  | "milestone_leads"
  | "storage_limit"
  | "tour_unpublished"
  | "scheduled_published"
  | "payment_failed"
  | "system";

export const NOTIFICATION_TYPES: NotificationType[] = [
  "new_lead",
  "new_booking",
  "booking_confirmed",
  "booking_reminder",
  "milestone_visits",
  "milestone_leads",
  "storage_limit",
  "tour_unpublished",
  "scheduled_published",
  "payment_failed",
];

/** Producto del que puede originarse cada notificación. Email se habilita si el
 *  dueño está entitled en ese producto. */
export type NotifProduct = "360";
export const NOTIFICATION_PRODUCTS: Record<NotificationType, NotifProduct[]> = {
  new_lead: ["360"],
  new_booking: ["360"],
  booking_confirmed: ["360"],
  booking_reminder: ["360"],
  milestone_visits: ["360"],
  milestone_leads: ["360"],
  storage_limit: ["360"],
  tour_unpublished: ["360"],
  scheduled_published: ["360"],
  payment_failed: ["360"],
  system: ["360"],
};

export const VISIT_THRESHOLDS = [50, 100, 250, 500, 1000, 5000];

export function crossedThreshold(count: number, thresholds: number[]): number | null {
  const crossed = thresholds.filter((t) => t <= count);
  return crossed.length ? Math.max(...crossed) : null;
}

export type EmailPref = "instant" | "daily" | "off";
export interface Preference {
  in_app: boolean;
  email: EmailPref;
}

// ponytail: email is on/off in the UI now (instant vs off); "daily" stays in
// the enum for legacy rows but isn't a default or a selectable option anymore.
export function defaultPreference(): Preference {
  return { in_app: true, email: "instant" };
}

export function notificationRoute(
  type: NotificationType,
  meta: Record<string, unknown>,
): string {
  switch (type) {
    case "new_lead":
    case "milestone_leads":
      return buildRoute.leads();
    case "new_booking":
    case "booking_confirmed":
    case "booking_reminder":
      return buildRoute.bookings();
    case "milestone_visits":
      return buildRoute.analytics();
    case "storage_limit":
    case "payment_failed":
      return buildRoute.billing();
    case "tour_unpublished":
    case "scheduled_published":
      return meta.tour_id
        ? buildRoute.detail(String(meta.tour_id))
        : buildRoute.tours();
    default:
      return buildRoute.overview();
  }
}

export interface NotificationI18n {
  titleKey: string;
  bodyKey: string;
  icon: string; // lucide icon name
}

export function notificationI18n(type: NotificationType): NotificationI18n {
  return {
    titleKey: `notif.${type}.title`,
    bodyKey: `notif.${type}.body`,
    icon: NOTIF_ICON[type] ?? "Bell",
  };
}

const NOTIF_ICON: Record<string, string> = {
  new_lead: "Inbox",
  milestone_leads: "Inbox",
  new_booking: "CalendarDays",
  booking_confirmed: "CalendarCheck",
  booking_reminder: "Clock",
  milestone_visits: "TrendingUp",
  storage_limit: "HardDrive",
  tour_unpublished: "EyeOff",
  scheduled_published: "Globe",
  payment_failed: "CreditCard",
};
